import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import { toast } from "sonner";
import { ArrowLeft, BedDouble, Bath, Users, MapPin } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api, formatApiError } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PhotoGallery from "../components/PhotoGallery";

const fieldCls =
  "w-full bg-transparent border-b border-[#0A1128]/20 focus:border-[#C5A059] outline-none py-3 text-[#0A1128] placeholder:text-[#3A435E]/40 text-sm tracking-wide";

// enumerate YYYY-MM-DD strings from [ci, co) as Date objects
const enumerateRange = (ciStr, coStr) => {
  const ci = new Date(ciStr);
  const co = new Date(coStr);
  const out = [];
  for (let d = new Date(ci); d < co; d.setDate(d.getDate() + 1)) {
    out.push(new Date(d));
  }
  return out;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const ReservePage = () => {
  const { t, lang } = useLang();
  const { houseId } = useParams();
  const navigate = useNavigate();
  const [house, setHouse] = useState(null);
  const [occupied, setOccupied] = useState([]);
  const [range, setRange] = useState(undefined);
  const [guests, setGuests] = useState(2);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/houses/${houseId}/public`),
      api.get(`/houses/${houseId}/availability`),
    ])
      .then(([h, a]) => {
        setHouse(h.data);
        setOccupied(a.data);
      })
      .catch(() => {
        toast.error("House not found");
        navigate("/");
      });
  }, [houseId, navigate]);

  const disabledDates = useMemo(() => {
    const out = [];
    occupied.forEach((o) => {
      out.push(...enumerateRange(o.check_in, o.check_out));
    });
    // also disable past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [{ before: today }, ...out];
  }, [occupied]);

  const nights = useMemo(() => {
    if (!range?.from || !range?.to) return 0;
    const ms = range.to - range.from;
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  }, [range]);

  // Fetch dynamic price quote from backend when valid range selected
  useEffect(() => {
    if (!range?.from || !range?.to || nights < 1) {
      setQuote(null);
      return;
    }
    const ci = range.from.toISOString().slice(0, 10);
    const co = range.to.toISOString().slice(0, 10);
    let cancelled = false;
    api.get(`/houses/${houseId}/quote`, { params: { check_in: ci, check_out: co } })
      .then(({ data }) => { if (!cancelled) setQuote(data); })
      .catch(() => { if (!cancelled) setQuote(null); });
    return () => { cancelled = true; };
  }, [range, nights, houseId]);

  const rangeHasConflict = useMemo(() => {
    if (!range?.from || !range?.to) return false;
    const selected = enumerateRange(
      range.from.toISOString().slice(0, 10),
      range.to.toISOString().slice(0, 10),
    );
    for (const s of selected) {
      for (const o of occupied) {
        const occ = enumerateRange(o.check_in, o.check_out);
        if (occ.some((od) => sameDay(od, s))) return true;
      }
    }
    return false;
  }, [range, occupied]);

  const submit = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to || nights < 1) {
      toast.error(t.reserve.select_dates);
      return;
    }
    if (rangeHasConflict) {
      toast.error(t.reserve.conflict);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/checkout-session", {
        house_id: houseId,
        check_in: range.from.toISOString().slice(0, 10),
        check_out: range.to.toISOString().slice(0, 10),
        guests_count: Number(guests),
        name: form.name,
        email: form.email,
        phone: form.phone,
        origin_url: window.location.origin,
        lang,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error(t.reserve.error);
    } catch (err) {
      const status = err.response?.status;
      toast.error(
        status === 409
          ? t.reserve.conflict
          : formatApiError(err.response?.data?.detail) || t.reserve.error
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!house) {
    return (
      <div className="bg-[#F4F1EB] min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 max-w-7xl mx-auto px-6 md:px-12 py-20 text-[#3A435E]">…</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#F4F1EB] min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-6 md:px-12 py-12 md:py-20" data-testid="reserve-page">
        <Link
          to="/#houses"
          className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#3A435E] hover:text-[#0A1128] transition-colors mb-10"
          data-testid="reserve-back"
        >
          <ArrowLeft size={14} strokeWidth={1.4} /> {t.reserve.back}
        </Link>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* House hero */}
          <div className="lg:col-span-5">
            <PhotoGallery cover={house.cover_image} photos={house.photos || []} name={house.name} />
            <div className="mt-6">
              <div className="overline flex items-center gap-2">
                <MapPin size={12} strokeWidth={1.4} /> {house.location}
              </div>
              <h1 className="font-serif-display text-4xl md:text-5xl font-light text-[#0A1128] leading-tight mt-3">
                {t.reserve.title_prefix} — <span className="italic">{house.name}</span>
              </h1>
              <p className="mt-6 text-[#3A435E] font-light leading-relaxed">{house.description}</p>
              <div className="mt-6 flex items-center gap-6 text-sm text-[#3A435E]">
                <span className="flex items-center gap-2"><Users size={14} strokeWidth={1.4} /> {house.max_guests} {t.houses.guests}</span>
                <span className="flex items-center gap-2"><BedDouble size={14} strokeWidth={1.4} /> {house.bedrooms}</span>
                <span className="flex items-center gap-2"><Bath size={14} strokeWidth={1.4} /> {house.bathrooms}</span>
              </div>
              {house.nightly_price > 0 && (
                <div className="mt-8 pt-6 border-t border-[#0A1128]/10 flex items-baseline gap-3">
                  <div className="font-serif-display text-4xl md:text-5xl font-light text-[#C5A059]" data-testid="reserve-nightly-price">
                    €{Math.round(house.nightly_price)}
                  </div>
                  <div className="text-xs tracking-[0.2em] uppercase text-[#3A435E]">
                    {t.houses.per_night}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Availability + form */}
          <div className="lg:col-span-7">
            <div className="overline mb-4">{t.reserve.overline}</div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-light leading-tight text-[#0A1128]">
              {t.reserve.availability}
            </h2>
            <p className="mt-3 text-[#3A435E] font-light max-w-lg">{t.reserve.subtitle}</p>

            <div className="mt-8 bg-[#FCFAF8] border border-[#0A1128]/10 p-5 md:p-8" data-testid="reserve-calendar">
              <DayPicker
                mode="range"
                numberOfMonths={2}
                selected={range}
                onSelect={setRange}
                disabled={disabledDates}
                locale={undefined}
                weekStartsOn={1}
                classNames={{
                  months: "flex flex-col md:flex-row gap-8",
                  month: "space-y-3",
                  caption: "flex justify-center relative items-center",
                  caption_label: "font-serif-display text-xl text-[#0A1128]",
                  nav_button: "h-7 w-7 bg-transparent hover:text-[#C5A059] opacity-70 hover:opacity-100 transition",
                  nav_button_previous: "absolute left-1",
                  nav_button_next: "absolute right-1",
                  table: "w-full border-collapse",
                  head_row: "flex",
                  head_cell: "text-[#7B86A4] uppercase tracking-widest text-[10px] w-9 py-2 font-normal",
                  row: "flex w-full mt-1",
                  cell: "relative p-0 text-center text-sm",
                  day: "h-9 w-9 p-0 font-normal rounded-none no-underline hover:bg-[#C5A059]/15 transition-colors",
                  day_selected: "bg-[#0A1128] text-white no-underline hover:bg-[#162243] hover:text-white",
                  day_range_middle: "bg-[#C5A059]/25 text-[#0A1128] no-underline",
                  day_range_start: "bg-[#0A1128] text-white no-underline",
                  day_range_end: "bg-[#0A1128] text-white no-underline",
                  day_today: "border border-[#C5A059]/60 no-underline",
                  day_disabled: "text-[#0A1128]/25 cursor-not-allowed no-underline",
                  day_outside: "text-[#0A1128]/25 no-underline",
                }}
              />
              <div className="mt-6 flex flex-wrap items-center gap-5 text-xs text-[#3A435E] border-t border-[#0A1128]/10 pt-5">
                <span className="flex items-center gap-2"><span className="w-3 h-3 bg-[#FCFAF8] border border-[#0A1128]/20 inline-block"></span>{t.reserve.legend_available}</span>
                <span className="flex items-center gap-2"><span className="w-3 h-3 inline-block" style={{background:"repeating-linear-gradient(45deg,#0A112825,#0A112825 2px,transparent 2px,transparent 4px)"}}></span>{t.reserve.legend_occupied}</span>
                <span className="flex items-center gap-2"><span className="w-3 h-3 bg-[#0A1128] inline-block"></span>{t.reserve.legend_selected}</span>
                {nights > 0 && (
                  <span className="ml-auto font-serif-display text-lg text-[#C5A059]">
                    {nights} {t.reserve.nights}
                  </span>
                )}
              </div>
            </div>

            <form onSubmit={submit} className="mt-8 bg-[#FCFAF8] border border-[#0A1128]/10 p-6 md:p-8 space-y-6" data-testid="reserve-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="overline block mb-2">{t.reserve.check_in}</label>
                  <div className="py-3 text-[#0A1128] border-b border-[#0A1128]/20 text-sm" data-testid="reserve-checkin">
                    {range?.from ? range.from.toLocaleDateString(lang === "pt" ? "pt-PT" : "en-US") : "—"}
                  </div>
                </div>
                <div>
                  <label className="overline block mb-2">{t.reserve.check_out}</label>
                  <div className="py-3 text-[#0A1128] border-b border-[#0A1128]/20 text-sm" data-testid="reserve-checkout">
                    {range?.to ? range.to.toLocaleDateString(lang === "pt" ? "pt-PT" : "en-US") : "—"}
                  </div>
                </div>
              </div>

              <div>
                <label className="overline block mb-2">{t.reserve.guests_count}</label>
                <input
                  type="number"
                  min={1}
                  max={house.max_guests}
                  value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  className={fieldCls}
                  data-testid="reserve-guests"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="overline block mb-2">{t.reserve.name}</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls} data-testid="reserve-name" />
                </div>
                <div>
                  <label className="overline block mb-2">{t.reserve.email}</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={fieldCls} data-testid="reserve-email" />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">{t.reserve.phone}</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={fieldCls} data-testid="reserve-phone" />
              </div>
              <div>
                <label className="overline block mb-2">{t.reserve.message}</label>
                <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${fieldCls} resize-none`} data-testid="reserve-message" />
              </div>

              <button
                type="submit"
                disabled={submitting || nights < 1}
                className="w-full md:w-auto bg-[#C5A059] text-[#0A1128] hover:bg-[#DCC287] transition-colors duration-300 px-8 py-4 text-xs tracking-[0.22em] uppercase disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="reserve-submit"
              >
                {submitting ? t.reserve.paying : t.reserve.submit}
              </button>

              {house.nightly_price > 0 && nights > 0 && (
                <div className="border-t border-[#0A1128]/10 pt-6 mt-6 space-y-2" data-testid="reserve-total-block">
                  {quote?.ai_pricing ? (
                    <div className="flex justify-between text-sm text-[#3A435E]">
                      <span>{nights} {t.reserve.nights} · preço dinâmico IA (média €{Math.round(quote.avg_nightly)}/noite)</span>
                      <span>€{Math.round(quote.total)}</span>
                    </div>
                  ) : (
                    <div className="flex justify-between text-sm text-[#3A435E]">
                      <span>€{Math.round(house.nightly_price)} × {nights} {t.reserve.nights}</span>
                      <span>€{Math.round((quote?.total) ?? (house.nightly_price * nights))}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline">
                    <span className="overline">{t.reserve.total}</span>
                    <span className="font-serif-display text-3xl md:text-4xl font-light text-[#0A1128]" data-testid="reserve-total">
                      €{Math.round((quote?.total) ?? (house.nightly_price * nights))}
                    </span>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ReservePage;
